import { useTranslation } from 'react-i18next';
import { GlassCard } from '@/components/glass';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { localizedName } from '@/utils/localized';
import type { MonthlySummary } from '../types';

interface MonthlySummaryCardProps {
  summary: MonthlySummary;
}

export function MonthlySummaryCard({ summary }: MonthlySummaryCardProps) {
  const { t, i18n } = useTranslation();
  const sym = useCurrencySymbol();
  const isHe = i18n.language === 'he';

  return (
    <GlassCard title={t('dashboard.monthlySummary')} padding="compact" contentClassName="space-y-2">
      {summary.byReportType.map((rt) => (
        <div key={rt.code} className="flex items-center justify-between rounded-lg border border-white/30 bg-white/20 px-3 py-2">
          <div>
            <span className="text-sm font-medium text-neutral-800">
              {isHe ? rt.name : rt.nameEn}
            </span>
            <span className="ms-1.5 text-xs text-neutral-500">({rt.count})</span>
          </div>
          <span className="text-sm font-semibold text-slate-800">{rt.earnings.toLocaleString()} {sym}</span>
        </div>
      ))}

      <div className="mt-3 flex items-center justify-between border-t-[2px] border-white/30 pt-3">
        <span className="text-sm font-semibold text-neutral-800">{t('dashboard.grandTotal')}</span>
        <span className="text-lg font-bold text-slate-800">{summary.grandTotal.toLocaleString()} {sym}</span>
      </div>

      {/* By hospital breakdown */}
      {summary.byHospital.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-medium text-neutral-500">{t('dashboard.byHospital')}</h4>
          <div className="space-y-1">
            {summary.byHospital.map((h) => (
              <div key={h.code} className="flex items-center justify-between px-3 py-1.5 text-xs">
                <span className="font-medium text-neutral-800">{localizedName(h, i18n.language)}</span>
                <span className="font-medium text-neutral-800">{Math.round(h.earnings).toLocaleString()} {sym}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </GlassCard>
  );
}
